import React, { useState, useRef, useEffect } from "react";
import {
    X, ChevronDown, Server, Package, Download, Hash, Monitor, Wrench, Terminal, Plus, AlertCircle, RefreshCw
} from "lucide-react";
import MultiSelectApiDropdown from "../../components/MultiSelectApiDropdown";
import baseapi from "../../services/baseapi";
import { showError, showSuccess } from "../../utils/toast";
import { useClientLevel } from "../../hooks/useClientLevel";

interface PatchFormData {
    application_name: string;
    download_url: string;
    hash: string;
    os_type: string;
    assets: any[];
    installer_type: string;
    silent_install_cmd: string;
}

interface PatchFormErrors {
    application_name?: string;
    download_url?: string;
    hash?: string;
    os_type?: string;
    assets?: string;
    installer_type?: string;
    silent_install_cmd?: string;
}

interface AddPatchFormProps {
    isOpen: boolean;
    onClose: () => void;
}

const AddPatchForm: React.FC<AddPatchFormProps> = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState<PatchFormData>({
        application_name: "",
        download_url: "",
        hash: "",
        os_type: "Windows",
        assets: [],
        installer_type: "",
        silent_install_cmd: "",
    });

    const [errors, setErrors] = useState<PatchFormErrors>({});
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const level = useClientLevel();
    const clientId = level?.id;

    const osOptions = [
        { value: "Windows", label: "Windows" },
        { value: "Linux", label: "Linux" },
        { value: "macOS", label: "macOS" },
    ];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const validateField = (name: keyof PatchFormData, value: any): string => {
        switch (name) {
            case "application_name":
                if (!value.trim()) return "Application name is required";
                return "";
            case "download_url":
                if (!value.trim()) return "Download URL is required";
                if (!value.startsWith("https://")) return "URL must start with https://";
                return "";
            case "hash":
                if (!value.trim()) return "SHA256 hash is required";
                if (!/^[a-fA-F0-9]{64}$/.test(value)) return "Invalid SHA256 hash";
                return "";
            case "os_type":
                return !value ? "Operating system is required" : "";
            case "assets":
                return !value || value.length === 0 ? "At least one asset is required" : "";
            case "installer_type":
                return !value ? "Installer type is required" : "";
            case "silent_install_cmd":
                if (!value.trim()) return "Silent install command is required";
                return "";
            default:
                return "";
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (showValidation) {
            setErrors((prev) => ({ ...prev, [name]: validateField(name as keyof PatchFormData, value) }));
        }
    };

    const handleOsSelect = (value: string) => {
        setFormData((prev) => ({ ...prev, os_type: value, assets: [] }));
        setIsDropdownOpen(false);
    };

    const handleAssetsChange = (items: any[]) => {
        setFormData((prev) => ({ ...prev, assets: items }));
        if (showValidation) {
            setErrors((prev) => ({ ...prev, assets: validateField("assets", items) }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: PatchFormErrors = {};
        let isValid = true;
        (Object.keys(formData) as Array<keyof PatchFormData>).forEach((key) => {
            const error = validateField(key, formData[key]);
            if (error) {
                newErrors[key] = error;
                isValid = false;
            }
        });
        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        setShowValidation(true);
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const params = {
                application_name: formData.application_name,
                download_url: formData.download_url,
                checksum: formData.hash,
                os_type: formData.os_type.toLowerCase(),
                installation_cmd: formData.silent_install_cmd,
                agent_ids: JSON.stringify(formData.assets),
                installer_type: formData.installer_type,
                client_id: clientId
            };
            const response = await baseapi.post('/application_patch', params);
            if (response.data.status) {
                showSuccess(response.data.message);
                onClose();
                // Reset form
                setFormData({
                    application_name: "",
                    download_url: "",
                    hash: "",
                    os_type: "Windows",
                    assets: [],
                    installer_type: "",
                    silent_install_cmd: "",
                });
                setShowValidation(false);
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            showError(error.message || "Failed to add patch");
        } finally {
            setIsSubmitting(false);
        }
    };



    return (
        <>
            {/* Backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] transition-opacity"
                    onClick={onClose}
                />
            )}

            {/* Sliding Panel */}
            <div
                className={`fixed top-0 right-0 h-full w-[35%] min-w-[360px] bg-gray-900 border-l border-gray-700 shadow-2xl z-[100] transform transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-800 shrink-0 bg-gray-900/50">
                    <div className="flex flex-col">
                        <h2 className="text-sm font-bold text-white uppercase tracking-widest flex items-center gap-2">
                            <Plus className="w-4 h-4 text-orange-400" />
                            Add New Patch
                        </h2>
                        <span className="text-[10px] text-gray-400 mt-1 uppercase tracking-widest">
                            Deploy application patches
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scroll">

                    {/* Patch Mode Toggle */}


                    <div className="space-y-4">
                        {/* Application Name */}
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                                <Package size={12} className="text-orange-400" />
                                Application Name <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                name="application_name"
                                value={formData.application_name}
                                onChange={handleChange}
                                placeholder="e.g., Google Chrome"
                                className={`w-full px-3 py-2 bg-gray-800/40 border rounded-lg text-white text-[11px] font-medium focus:border-orange-500 outline-none transition-all ${errors.application_name ? 'border-red-500 bg-red-500/5' : 'border-gray-700 hover:border-gray-600'}`}
                            />
                            {errors.application_name && <p className="text-red-400 text-[9px] mt-1 flex items-center gap-1 font-bold uppercase tracking-wider"><AlertCircle size={10} /> {errors.application_name}</p>}
                        </div>

                        {/* Download URL */}
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                                <Download size={12} className="text-orange-400" />
                                Download URL <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="url"
                                name="download_url"
                                value={formData.download_url}
                                onChange={handleChange}
                                placeholder="https://..."
                                className={`w-full px-3 py-2 bg-gray-800/40 border rounded-lg text-white text-[11px] font-medium focus:border-orange-500 outline-none transition-all ${errors.download_url ? 'border-red-500 bg-red-500/5' : 'border-gray-700 hover:border-gray-600'}`}
                            />
                            {errors.download_url && <p className="text-red-400 text-[9px] mt-1 flex items-center gap-1 font-bold uppercase tracking-wider"><AlertCircle size={10} /> {errors.download_url}</p>}
                        </div>

                        {/* Checksum */}
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                                <Hash size={12} className="text-orange-400" />
                                SHA256 Checksum <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                name="hash"
                                value={formData.hash}
                                onChange={handleChange}
                                placeholder="64-char hexadecimal hash"
                                className={`w-full px-3 py-2 bg-gray-800/40 border rounded-lg text-white text-[10px] font-mono focus:border-orange-500 outline-none transition-all ${errors.hash ? 'border-red-500 bg-red-500/5' : 'border-gray-700 hover:border-gray-600'}`}
                            />
                            {errors.hash && <p className="text-red-400 text-[9px] mt-1 flex items-center gap-1 font-bold uppercase tracking-wider"><AlertCircle size={10} /> {errors.hash}</p>}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            {/* OS Type */}
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                                    <Monitor size={12} className="text-orange-400" />
                                    OS Type <span className="text-red-400">*</span>
                                </label>
                                <div className="relative" ref={dropdownRef}>
                                    <button
                                        type="button"
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                        className={`w-full px-3 py-2 bg-gray-800/40 border rounded-lg text-white text-[11px] flex items-center justify-between hover:border-gray-600 transition-all ${errors.os_type ? 'border-red-500 bg-red-500/5' : 'border-gray-700'}`}
                                    >
                                        <span>{formData.os_type}</span>
                                        <ChevronDown size={12} className={`text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {isDropdownOpen && (
                                        <div className="absolute z-[60] w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl overflow-hidden">
                                            {osOptions.map((opt) => (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => handleOsSelect(opt.value)}
                                                    className={`w-full px-3 py-2 text-left text-[11px] transition-colors ${formData.os_type === opt.value ? 'bg-orange-600 text-white' : 'text-gray-300 hover:bg-gray-700/50'}`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Installer Type */}
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                                    <Wrench size={12} className="text-orange-400" />
                                    Installer <span className="text-red-400">*</span>
                                </label>
                                <div className="flex bg-gray-800/40 p-1 rounded-lg border border-gray-700 h-[34px]">
                                    {['msi', 'exe'].map((type) => (
                                        <button
                                            key={type}
                                            type="button"
                                            onClick={() => setFormData(p => ({ ...p, installer_type: type }))}
                                            className={`flex-1 text-[9px] font-bold uppercase rounded transition-all ${formData.installer_type === type ? 'bg-gray-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-300'}`}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                                {errors.installer_type && <p className="text-red-400 text-[9px] mt-1 font-bold uppercase tracking-wider"><AlertCircle size={9} className="inline mr-1" /> Required</p>}
                            </div>
                        </div>

                        {/* Assets Multi Select */}
                        <div className="space-y-1.5">
                            <MultiSelectApiDropdown
                                label="Target Assets"
                                value={formData.assets}
                                onChange={handleAssetsChange}
                                placeholder="Select target assets"
                                apiEndpoint="/agents"
                                icon={Server}
                                searchPlaceholder="Search assets"
                                searchInputPlaceholder="Search by hostname..."
                                error={errors.assets}
                                required={true}
                                displayProperty="hostname"
                                searchFields={["hostname"]}
                                idProperty="id"
                                className="text-[11px]"
                                additionalQueries={{
                                    platform: formData.os_type?.toLowerCase(),
                                    agent_type: "lightweight",
                                    ...(clientId ? { client_id: clientId } : {}),
                                }}
                            />
                        </div>

                        {/* Silent Install Command */}
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                                <Terminal size={12} className="text-orange-400" />
                                Silent Install Command <span className="text-red-400">*</span>
                            </label>
                            <textarea
                                name="silent_install_cmd"
                                value={formData.silent_install_cmd}
                                onChange={handleChange}
                                rows={2}
                                placeholder="/qn /norestart"
                                className={`w-full px-3 py-2 bg-gray-800/40 border rounded-lg text-white text-[11px] font-mono focus:border-orange-500 outline-none transition-all resize-none ${errors.silent_install_cmd ? 'border-red-500 bg-red-500/5' : 'border-gray-700 hover:border-gray-600'}`}
                            />
                            <div className="flex justify-between items-center">
                                {errors.silent_install_cmd && <p className="text-red-400 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1"><AlertCircle size={10} /> {errors.silent_install_cmd}</p>}
                                <span className="text-[9px] text-gray-600 font-bold ml-auto">{formData.silent_install_cmd.length}/300</span>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Footer Actions */}
                <div className="p-4 border-t border-gray-800 bg-gray-900 shrink-0 flex gap-2">

                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex-1 py-2.5 bg-orange-600 hover:bg-orange-500 disabled:bg-gray-800 disabled:text-gray-500 disabled:border disabled:border-gray-700 text-white text-[11px] font-bold uppercase tracking-widest rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                        {isSubmitting ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
                        Confirm & Deploy
                    </button>

                    <button
                        onClick={onClose}
                        className="px-4 bg-gray-800 text-gray-400 py-2.5 rounded-lg font-bold text-[11px] uppercase tracking-wider hover:bg-gray-700 hover:text-white transition-all border border-gray-700"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </>
    );
};

export default AddPatchForm;